import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionKey,
  type BravePublicSnapshot,
  type BraveRankingPreview,
  type BraveSaveInput,
  type BraveSnapshot,
  type BraveSnapshotKind,
  type BraveSnapshotSummary,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore, sessionVenueKey } from '../../store/auth.store';
import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import { braveCopy, braveError, braveCutoff } from './BraveRankingPage.copy';
import './BraveRankingPage.css';
import { BravePresetSection } from './BraveRankingPage.presets';
import { bravePresetCopy } from './BraveRankingPage.copy';
export default function BraveRankingPage() {
  const revision = useAuthStore((s) => s.sessionRevision);
  const venue = useAuthStore((state) => sessionVenueKey(state.accessToken));
  return <BraveWorkspace key={`${revision}:${venue}`} />;
}
function BraveWorkspace() {
  const { i18n, t } = useTranslation();
  const c = braveCopy(i18n.language ?? i18n.resolvedLanguage ?? 'en', t);
  const actions = useAuthStore((s) => s.allowedActions) ?? [];
  const canRead = actions.includes(ActionKey.BRAVE_RANKING_READ);
  const canManage = canRead && actions.includes(ActionKey.BRAVE_RANKING_MANAGE);
  const [readDenied, setReadDenied] = useState(false);
  const [period, setPeriod] = useState(() =>
    new Date(Date.now() + 28800000).toISOString().slice(0, 7),
  );
  const [cutoff, setCutoff] = useState(''),
    [businessDate, setBusinessDate] = useState(''),
    [search, setSearch] = useState('');
  const [kind, setKind] = useState<BraveSnapshotKind>('ANNOUNCEMENT'),
    [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false),
    [closure, setClosure] = useState(false);
  const [preview, setPreview] = useState<BraveRankingPreview | null>(null),
    [selected, setSelected] = useState<BraveSnapshot | null>(null),
    [publicList, setPublicList] = useState<BravePublicSnapshot | null>(null);
  const [history, setHistory] = useState<BraveSnapshotSummary[]>([]),
    [error, setError] = useState(''),
    [notice, setNotice] = useState(''),
    [busy, setBusy] = useState(false);
  const epoch = useRef(0),
    authorityEpoch = useRef(0),
    busyRef = useRef(false),
    pending = useRef<{ key: string; body: BraveSaveInput } | null>(null);
  const invalidate = useCallback(() => {
    epoch.current++;
    setPreview(null);
    setSelected(null);
    setPublicList(null);
    setConfirmed(false);
    setNotice('');
    setError('');
    pending.current = null;
  }, []);
  const clearDenied = useCallback(
    (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 401 && status !== 403) return false;
      authorityEpoch.current++;
      setReadDenied(true);
      invalidate();
      setHistory([]);
      setClosure(false);
      setReason('');
      setSearch('');
      busyRef.current = false;
      setBusy(false);
      setError(c.denied);
      return true;
    },
    [invalidate, c.denied],
  );
  useEffect(() => {
    let live = true;
    const authority = authorityEpoch.current;
    setHistory([]);
    if (canRead)
      api
        .get<BraveSnapshotSummary[]>('/brave-ranking/snapshots', { params: { period } })
        .then((rows) => {
          if (live && authority === authorityEpoch.current) setHistory(rows);
        })
        .catch((error) => {
          if (live && authority === authorityEpoch.current && !clearDenied(error))
            setError(c.historyError);
        });
    return () => {
      live = false;
    };
  }, [period, canRead, c.historyError, clearDenied]);
  useEffect(
    () => () => {
      epoch.current++;
    },
    [],
  );
  async function run(work: (generation: number) => Promise<void>) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    const generation = epoch.current;
    try {
      await work(generation);
    } catch (e) {
      if (generation === epoch.current) {
        if (clearDenied(e)) return;
        const data = (
          e as { response?: { data?: { code?: string; errorCode?: string; message?: string } } }
        ).response?.data;
        setError(braveError(data?.errorCode ?? data?.code ?? data?.message ?? '', c));
      }
    } finally {
      if (generation === epoch.current) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  }
  const input = () => ({
    period,
    cutoff: `${cutoff}+08:00`,
    ...(businessDate ? { lastBusinessDate: businessDate } : {}),
  });
  function loadPreview() {
    if (!cutoff) {
      setError(c.cutoffRequired);
      return;
    }
    setConfirmed(false);
    pending.current = null;
    void run(async (generation) => {
      const value = await api.get<BraveRankingPreview>('/brave-ranking/preview', {
        params: input(),
      });
      if (generation !== epoch.current) return;
      setPreview(value);
      setSelected(null);
      setPublicList(null);
    });
  }
  function save() {
    if (!preview) {
      setError(c.previewFirst);
      return;
    }
    void run(async (generation) => {
      const candidate = {
        ...input(),
        kind,
        reason,
        closureConfirmed: closure,
        expectedVersion: preview.latestVersion,
        previewDigest: preview.previewDigest,
      };
      const key = JSON.stringify(candidate);
      if (pending.current?.key !== key)
        pending.current = { key, body: { ...candidate, operationKey: crypto.randomUUID() } };
      const saved = await api.post<BraveSnapshot>('/brave-ranking/snapshots', pending.current.body);
      if (generation !== epoch.current) return;
      setSelected(saved);
      setPreview(null);
      setConfirmed(false);
      setNotice(c.saved);
      pending.current = null;
      const [versions, pub] = await Promise.all([
        api.get<BraveSnapshotSummary[]>('/brave-ranking/snapshots', { params: { period } }),
        api.get<BravePublicSnapshot>(`/brave-ranking/snapshots/${saved.id}/public`),
      ]);
      if (generation === epoch.current) {
        setHistory(versions);
        setPublicList(pub);
      }
    });
  }
  function reopen(id: string) {
    void run(async (generation) => {
      const [saved, pub] = await Promise.all([
        api.get<BraveSnapshot>(`/brave-ranking/snapshots/${id}`),
        api.get<BravePublicSnapshot>(`/brave-ranking/snapshots/${id}/public`),
      ]);
      if (generation === epoch.current) {
        setSelected(saved);
        setCutoff(braveCutoff(saved.cutoff).slice(0, 19).replace(' ', 'T'));
        setBusinessDate(saved.lastBusinessDate ?? '');
        setPublicList(pub);
        setPreview(null);
        setConfirmed(false);
      }
    });
  }
  function download() {
    if (!publicList) return;
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(publicList, null, 2)], { type: 'application/json' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `brave-${publicList.period}-v${publicList.version}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  const rows = (preview?.rows ?? selected?.rows ?? []).filter((r) =>
    r.nickname.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
  );
  if (!canRead || readDenied)
    return (
      <AdminPage header={<AdminPageHeader title={c.title} />}>
        <p role="alert">{c.denied}</p>
      </AdminPage>
    );
  return (
    <AdminPage
      className="brave-page"
      contentClassName="space-y-5"
      header={<AdminPageHeader title={c.title} description={c.intro} />}
    >
      <BravePresetSection
        language={i18n.language ?? i18n.resolvedLanguage ?? 'en'}
        onDenied={clearDenied}
      />
      <h2>{bravePresetCopy(i18n.language ?? i18n.resolvedLanguage ?? 'en', t).official}</h2>
      <section className="brave-controls" aria-label={c.preview}>
        <label>
          {c.period}
          <input
            type="month"
            value={period}
            disabled={busy}
            onChange={(e) => {
              invalidate();
              setPeriod(e.target.value);
              setBusinessDate('');
            }}
          />
        </label>
        <label>
          {c.cutoff}
          <input
            type="datetime-local"
            value={cutoff}
            disabled={busy}
            onChange={(e) => {
              invalidate();
              setCutoff(e.target.value);
            }}
          />
        </label>
        <label>
          {c.businessDate}
          <input
            type="date"
            value={businessDate}
            disabled={busy}
            onChange={(e) => {
              invalidate();
              setBusinessDate(e.target.value);
            }}
          />
        </label>
        <button type="button" disabled={busy} onClick={loadPreview}>
          {busy ? c.busy : c.preview}
        </button>
      </section>
      {error && (
        <p role="alert" className="brave-message">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {preview?.issues.length ? (
        <p role="alert">
          {c.source} ({preview.issues.length})
        </p>
      ) : null}
      {preview && (
        <p>
          {c.rule} {c.count}: {preview.rows.filter((r) => r.qualified).length}
        </p>
      )}
      {(preview || selected) && (
        <section className="brave-staff-table">
          {selected && (
            <p>
              {c.version} {selected.version} · {c.cutoffLabel}: {braveCutoff(selected.cutoff)} ·{' '}
              {c.changed}: {selected.changedMembers}
            </p>
          )}
          <label>
            {c.search}
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
          </label>
          <div className="brave-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{c.rank}</th>
                  <th>{c.nickname}</th>
                  <th>{c.points}</th>
                  <th>{c.qualified}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.memberId}>
                    <td>{r.rank}</td>
                    <td>{r.nickname}</td>
                    <td>{r.points}</td>
                    <td>{r.qualified ? c.yes : c.no}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!rows.length && <p>{c.empty}</p>}
        </section>
      )}
      {canManage && preview && (
        <section className="brave-save" aria-label={c.save}>
          <label>
            {c.kind}
            <select
              value={kind}
              disabled={busy}
              onChange={(e) => {
                setKind(e.target.value as BraveSnapshotKind);
                setConfirmed(false);
              }}
            >
              <option value="ANNOUNCEMENT">{c.announcement}</option>
              <option value="FINAL">{c.final}</option>
            </select>
          </label>
          <label>
            {c.reason}
            <textarea
              maxLength={1000}
              value={reason}
              disabled={busy}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          {kind === 'FINAL' && (
            <>
              <p>{preview.finalizationIssues.length ? c.blocked : ''}</p>
              <label className="brave-check">
                <input
                  type="checkbox"
                  checked={closure}
                  disabled={busy}
                  onChange={(e) => setClosure(e.target.checked)}
                />
                {c.closure}
              </label>
            </>
          )}
          <label className="brave-check">
            <input
              type="checkbox"
              checked={confirmed}
              disabled={busy}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            {c.confirm}
          </label>
          <button
            type="button"
            onClick={save}
            disabled={
              busy ||
              !confirmed ||
              !!preview.issues.length ||
              (preview.latestVersion > 0 && !reason.trim()) ||
              (kind === 'FINAL' && (!closure || !!preview.finalizationIssues.length))
            }
          >
            {c.save}
          </button>
        </section>
      )}
      <section className="brave-history">
        <h2>{c.history}</h2>
        {!history.length && <p>{c.noHistory}</p>}
        {history.map((v) => (
          <div className="brave-version" key={v.id}>
            <span>
              {c.version} {v.version} · {v.kind === 'FINAL' ? c.final : c.announcement} ·{' '}
              {braveCutoff(v.cutoff)}
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => reopen(v.id)}
              aria-label={`${c.reopen} ${v.version}`}
            >
              {c.reopen}
            </button>
          </div>
        ))}
      </section>
      {publicList && (
        <>
          <div className="brave-export">
            <button type="button" onClick={() => window.print()}>
              {c.print}
            </button>
            <button type="button" onClick={download}>
              {c.download}
            </button>
          </div>
          <section className="brave-public">
            <h2>
              {c.publicList} — {publicList.period} · {c.version} {publicList.version}
            </h2>
            <p>
              {c.cutoffLabel}: {braveCutoff(publicList.cutoff)}
            </p>
            <table>
              <thead>
                <tr>
                  <th>{c.rank}</th>
                  <th>{c.nickname}</th>
                  <th>{c.points}</th>
                </tr>
              </thead>
              <tbody>
                {publicList.rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.rank}</td>
                    <td>{r.nickname}</td>
                    <td>{r.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </AdminPage>
  );
}
